import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/form/Input';
import { Select } from '@/components/form/Select';
import { Textarea } from '@/components/form/Textarea';
import { SearchableSelect } from '@/components/form/SearchableSelect';
import { ymd } from '@/components/ui/PeriodPicker';
import { useToast } from '@/components/ui/useToast';
import { apiError } from '@/lib/apiError';
import { GST_STATE_MAP } from '@/lib/validation';
import {
  BANK_MODES, alterExpense, createExpense, listCostCentres, listTdsSections, listVoucherTypes, tdsPreview, tradeSetup,
  type ExpenseInput, type PayVia,
} from '@/api/accountsBooks';
import { expBudgetCheck } from '@/api/accountsExp';
import { ChipTag, DateField, F, FormFooter, FormTitle, Msgs, Posting, ReadBox, Sec, type PostLine } from './voucherFormParts';
import { curBal, dd, inr, r2 } from './voucherFormat';
import { Grid } from './tradeParts';
import { addDays } from './tradeHelpers';
import type { FormProps } from './useVoucherForm';

/* Expense bill — the Journal's second mode, as the prototype: Party A/c (vendor, or none for
   petty / one-off bills) → Particulars laid out like a Tally journal (Dr expense head with its
   department, Dr Input GST, Cr TDS Payable, Cr vendor with the bill-wise New Ref) → Settlement
   (on credit, bank, cash or split) → Narration → Posting (the bill, and the payment when paid now). */

const GST = [0, 5, 12, 18, 28];
const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
interface State {
  date: string; vendor: number | ''; billNo: string; billDate: string; paidTo: string; sc: string;
  head: number | ''; dept: number | ''; taxable: number; g: number; itcOff: boolean; gstAmt: number | null;
  sec: string; tdsAmt: number | null; via: PayVia; bank: number | ''; mode: string; ref: string; bankAmt: number | null; narr: string;
}

export function ExpenseForm({ voucher, ledgers, onClose, onGeneral }: FormProps & { onGeneral?: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const byId = useMemo(() => new Map(ledgers.map((l) => [l.id, l])), [ledgers]);
  const vendors = ledgers.filter((l) => l.role === 'C');
  const heads = ledgers.filter((l) => l.expense && l.name !== 'Round Off');
  const banks = ledgers.filter((l) => l.kind === 'bank');
  const cashL = ledgers.find((l) => l.kind === 'cash');
  const ccQ = useQuery({ queryKey: ['acc-books', 'cost-centres'], queryFn: listCostCentres });
  const secQ = useQuery({ queryKey: ['acc-books', 'tds-sections'], queryFn: listTdsSections });
  const typesQ = useQuery({ queryKey: ['acc-books', 'voucher-types'], queryFn: listVoucherTypes });
  const setupQ = useQuery({ queryKey: ['acc-books', 'trade-setup'], queryFn: tradeSetup });
  const home = setupQ.data?.stateCode ?? '06';
  const today = ymd(new Date());
  const d = voucher?.details;

  const [s, setS] = useState<State>(() => (voucher && d
    ? {
      date: voucher.date, vendor: d.vendorLedgerId ?? '', billNo: d.billNo ?? '', billDate: d.billDate ?? voucher.date, paidTo: d.paidTo ?? '', sc: d.stateCode ?? '',
      head: d.headLedgerId ?? '', dept: d.costCentreId ?? '', taxable: d.taxable ?? 0, g: d.gstRate ?? 0, itcOff: !!d.gst && !d.itc && d.itcWhy === 'ITC switched off for this bill',
      gstAmt: d.gst != null && d.gst !== d.gstAuto ? d.gst : null, sec: d.tdsSection ?? '', tdsAmt: d.tds != null && d.tds !== d.tdsAuto ? d.tds : null,
      via: d.payVia ?? 'credit', bank: voucher.lines.find((l) => banks.some((b) => b.id === l.ledgerId))?.ledgerId ?? banks[0]?.id ?? '', mode: voucher.mode && voucher.mode !== 'Cash' ? voucher.mode : 'NEFT', ref: voucher.refNo ?? '', bankAmt: d.bankPart ?? null,
      narr: voucher.narration ?? '',
    }
    : { date: today, vendor: '', billNo: '', billDate: today, paidTo: '', sc: '', head: '', dept: '', taxable: 0, g: 18, itcOff: false, gstAmt: null, sec: '', tdsAmt: null, via: banks.length ? 'bank' : 'cash', bank: banks[0]?.id ?? '', mode: 'NEFT', ref: '', bankAmt: null, narr: '' }));
  const set = (p: Partial<State>) => setS((x) => ({ ...x, ...p }));

  const vendor = s.vendor === '' ? undefined : byId.get(s.vendor);
  const head = s.head === '' ? undefined : byId.get(s.head);
  /** A vendor: start on credit; GST off for an unregistered one. */
  const pickVendor = (id: number | '') => {
    const v = id === '' ? undefined : byId.get(id);
    set({ vendor: id, gstAmt: null, tdsAmt: null, itcOff: false, ...(v ? { via: 'credit' as PayVia, ...(v.gstin ? {} : { g: 0 }) } : { via: s.via === 'credit' ? (banks.length ? 'bank' : 'cash') : s.via, sec: '' }) });
  };
  /** An expense head brings its department, GST rate and TDS section (set on its ledger). */
  const pickHead = (id: number | '') => {
    const h = id === '' ? undefined : byId.get(id);
    const e = h?.expense;
    set({
      head: id, gstAmt: null, tdsAmt: null,
      ...(e?.costCentreId ? { dept: e.costCentreId } : {}),
      ...(e?.gstRate != null && !(vendor && !vendor.gstin) ? { g: e.gstRate } : {}),
      ...(vendor ? { sec: e?.tdsSection ?? '' } : {}),
    });
  };

  // The bill, worked out as saving will.
  const sc = vendor ? vendor.stateCode : s.sc || home;
  const intra = !(home && sc && home !== sc);
  const reg = vendor ? !!vendor.gstin : true;
  const g = reg ? s.g : 0;
  const gstAuto = r2((s.taxable * g) / 100);
  const gst = reg && s.gstAmt != null ? r2(s.gstAmt) : gstAuto;
  const blocked = head?.expense?.itcBlocked ?? null;
  const itc = gst > 0 && !blocked && !s.itcOff;
  const why = gst > 0 && !itc ? blocked ?? 'ITC switched off for this bill' : null;
  const cg = intra ? r2(gst / 2) : 0, sg = intra ? r2(gst - cg) : 0, ig = intra ? 0 : gst;
  const total = r2(s.taxable + gst);
  const tdsQ = useQuery({
    queryKey: ['acc-books', 'tds-preview', s.vendor, s.sec, s.taxable, s.date, voucher?.id ?? 0],
    queryFn: () => tdsPreview({ vendor: s.vendor as number, sec: s.sec, taxable: s.taxable, date: s.date, excludeVoucher: voucher?.id }),
    enabled: !!vendor && !!s.sec && s.taxable > 0 && !!s.date,
  });
  const tdsAuto = vendor && s.sec ? tdsQ.data?.tds ?? 0 : 0;
  const tds = vendor && s.sec ? (s.tdsAmt ?? tdsAuto) : 0;
  const net = r2(total - tds);
  const cost = r2(s.taxable + (itc ? 0 : gst));
  // The head's monthly budget: spent so far this month (this bill left out when altering) and after it.
  const budQ = useQuery({
    queryKey: ['acc-books', 'exp', 'budget-check', s.head, s.date.slice(0, 7), voucher?.id ?? 0],
    queryFn: () => expBudgetCheck({ ledger: s.head as number, date: s.date, excludeVoucher: voucher?.id }),
    enabled: s.head !== '' && !!s.date,
  });
  const bud = budQ.data?.budget ? budQ.data : null;
  const due = vendor ? net : total;
  const bankPart = s.via === 'split' ? r2(Math.min(due, Math.max(0, s.bankAmt ?? due))) : s.via === 'bank' ? due : 0;
  const cashPart = s.via === 'split' ? r2(due - bankPart) : s.via === 'cash' ? due : 0;
  const payNow = !!vendor && s.via !== 'credit';
  const credit = Math.max(0, vendor?.creditDays || 15);
  const dueOn = addDays(s.billDate || s.date, credit);
  const bankL = s.bank === '' ? undefined : byId.get(s.bank);
  const sections = secQ.data ?? [];
  const altering = !!voucher;

  const block: string[] = [];
  const warn: string[] = [];
  if (!(s.taxable > 0)) block.push('Enter the taxable amount.');
  if (!s.date) block.push('Enter the date.');
  else if (s.date > today) block.push(`The date can’t be after today (${dd(today)}).`);
  if (!head) block.push('Choose the expense head.');
  if (s.via === 'credit' && !vendor) block.push('Pick the vendor to book this bill on credit.');
  if (vendor && !s.billNo.trim()) block.push("Enter the vendor's bill number.");
  if (vendor && s.billDate > s.date) block.push('Supplier invoice date cannot be after the voucher date.');
  if (!vendor && !s.paidTo.trim()) block.push('Enter who was paid.');
  if (s.sec && !vendor) block.push('Pick the vendor — TDS needs their PAN and year-to-date total.');
  if (!altering && (s.via === 'bank' || s.via === 'split') && !bankL) block.push('Choose the bank account it is paid from.');
  if (!altering && (s.via === 'bank' || s.via === 'split') && !s.ref.trim()) block.push(s.via === 'split' ? 'Enter the UTR / cheque number of the bank part.' : 'Enter the UTR / cheque number of the payment.');
  if (s.via === 'split' && (!(bankPart > 0) || !(cashPart > 0))) block.push(`Split needs both parts — enter how much is paid from the bank; the rest (of ${inr(due)}) is cash.`);
  if (tds > total) block.push('TDS cannot be more than the bill total.');
  if (s.via === 'cash' && (payNow ? net : total) > 10000) warn.push('Cash payment above ₹10,000 to one person in a day is not allowed as a business expense (s.40A(3)). Pay by bank.');
  if (s.via === 'split' && cashPart > 10000) warn.push(`Cash part ${inr(cashPart)} is above ₹10,000 — cash over ₹10,000 to one person in a day is not allowed as a business expense (s.40A(3)). Pay more from the bank.`);
  if (s.gstAmt != null && Math.abs(gst - gstAuto) > 1) warn.push(`GST entered ${inr(gst)} differs from ${g}% of the taxable amount (${inr(gstAuto)}) — check it against the vendor's bill.`);
  if (s.tdsAmt != null && tdsQ.data && Math.abs(tds - tdsAuto) > 1) warn.push(`TDS entered ${inr(tds)} differs from the calculated ${inr(tdsAuto)} (${tdsQ.data.rate}% on ${inr(tdsQ.data.base)}).`);
  if (vendor && s.sec && !vendor.pan) warn.push(`${vendor.name} has no PAN in Party Master — without a PAN, TDS is due at 20% (s.206AA).`);

  // Posting — the bill, and the payment when paid now.
  const billLines: PostLine[] = [
    { l: head?.name ?? '', a: cost },
    ...(itc ? [{ l: 'Input CGST', a: cg }, { l: 'Input SGST', a: sg }, { l: 'Input IGST', a: ig }] : []),
    ...(vendor
      ? [{ l: vendor.name, a: -net, sub: [`New Ref ${s.billNo.trim() || '(bill no.)'} · due ${dd(dueOn)} · ${inr(net)}`] }, ...(tds ? [{ l: `TDS Payable – ${s.sec === 'RENT' ? 'Rent' : s.sec}`, a: -tds }] : [])]
      : [{ l: bankL?.name ?? 'Bank', a: -bankPart }, { l: cashL?.name ?? 'Cash', a: -cashPart }]),
  ];
  const payLines: PostLine[] = payNow ? [{ l: vendor!.name, a: net, sub: [`Agst Ref ${s.billNo.trim()}`] }, { l: bankL?.name ?? 'Bank', a: -bankPart }, { l: cashL?.name ?? 'Cash', a: -cashPart }] : [];
  const vtype = vendor || s.via === 'credit' ? 'Journal' : 'Payment';
  const no = voucher?.voucherNo ?? typesQ.data?.find((t) => t.name === vtype)?.nextNo ?? '—';

  const build = (): ExpenseInput => ({
    date: s.date, headLedgerId: s.head as number, costCentreId: s.dept === '' ? null : s.dept, taxable: s.taxable, gstRate: g, claimItc: !s.itcOff,
    gstAmount: s.gstAmt, tdsAmount: vendor && s.sec ? s.tdsAmt : null,
    vendorLedgerId: vendor ? vendor.id : null, ...(vendor ? { billNo: s.billNo.trim(), billDate: s.billDate } : { paidTo: s.paidTo.trim(), stateCode: s.sc || null }),
    tdsSection: vendor && s.sec ? s.sec : null, payVia: s.via, bankLedgerId: s.bank === '' ? null : s.bank, cashLedgerId: cashL?.id ?? null,
    mode: s.mode, refNo: s.ref.trim(), bankAmount: s.via === 'split' ? bankPart : null, narration: s.narr.trim(),
  });
  const save = useMutation({
    mutationFn: (b: ExpenseInput) => (voucher ? alterExpense(voucher.id, b).then((v) => ({ bill: v, payments: [] })) : createExpense(b)),
    onSuccess: (r) => {
      toast.push(`Expense ${r.bill.voucherNo}${r.payments.length ? ` and ${r.payments.map((p) => p.voucherNo).join(', ')}` : ''} ${voucher ? 'altered' : 'saved'}`, 'success');
      qc.invalidateQueries({ queryKey: ['acc-books'] });
      onClose();
    },
    onError: (e) => toast.push(apiError(e, 'Save failed'), 'error'),
  });

  const amt = (val: number, on: (v: number) => void, dis = false) => (
    <Input type="number" step="0.01" min={0} disabled={dis} className="ml-auto !w-[140px] text-right font-mono" value={val || ''} placeholder="0.00" onChange={(e) => on(r2(Number(e.target.value) || 0))} />
  );
  const edited = (on: boolean, reset: () => void) => (on ? <button type="button" onClick={reset} className="ml-1 text-[11px] font-bold text-accent" title="Use the calculated amount">· edited ↺ auto</button> : null);
  const viaValue = s.via === 'bank' || s.via === 'split' ? `${s.via}:${s.bank}` : s.via;

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={
        <FormTitle title={vtype} no={no} chip={<ChipTag tone="r">Expense bill</ChipTag>}
          extra={
            <span className="ml-auto inline-flex overflow-hidden rounded-md border border-[var(--border-color)] text-[11px] font-bold">
              <button type="button" disabled={!onGeneral} onClick={onGeneral} className="px-2.5 py-1 text-[var(--text-muted)] hover:text-accent disabled:cursor-not-allowed">General journal</button>
              <span className="bg-accent/15 px-2.5 py-1 text-accent">Expense bill (GST + TDS)</span>
            </span>
          } />
      }
      footer={
        <FormFooter left={vendor ? <>Payable to vendor <b>{inr(net)}</b></> : <>Paid <b>{inr(total)}</b></>} version={voucher ? voucher.version + 1 : undefined}
          saveLabel="Save Expense" blocked={block.length > 0} saving={save.isPending} onClose={onClose} onSave={() => save.mutate(build())} />
      }
    >
      <Grid>
        <DateField value={s.date} max={today} onChange={(v) => set({ date: v, tdsAmt: null })} />
        <F label="Party A/c (vendor)" className="col-span-2">
          <SearchableSelect
            options={[{ value: 0, label: '— None (petty / one-off) —' }, ...vendors.map((v) => ({ value: v.id, label: v.name, sublabel: [v.partyCode, v.gstin ?? 'Unregistered'].filter(Boolean).join(' · ') }))]}
            value={s.vendor === '' ? 0 : s.vendor} placeholder="Vendor"
            onChange={(v) => pickVendor(!v ? '' : Number(v))}
          />
        </F>
        {vendor ? <F label="Cur Bal"><ReadBox value={curBal(vendor)} /></F> : (
          <>
            <F label="Supplier state">
              <Select value={s.sc || home} onChange={(e) => set({ sc: e.target.value })}>
                {Object.entries(GST_STATE_MAP).map(([c, nm]) => <option key={c} value={c}>{nm}</option>)}
              </Select>
            </F>
            <F label="Paid to *" className="col-span-2"><Input maxLength={120} value={s.paidTo} placeholder="e.g. Local conveyance" onChange={(e) => set({ paidTo: e.target.value })} /></F>
          </>
        )}
      </Grid>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 rounded-md border border-info/30 bg-info/10 px-2.5 py-1.5 text-xs text-[var(--text-secondary)]">
        {vendor ? (
          <>
            <span>GSTIN <b className="font-mono">{vendor.gstin || 'Not registered'}</b></span>
            <span>State <b>{GST_STATE_MAP[vendor.stateCode ?? ''] ?? vendor.stateCode ?? '—'}</b> → <b>{intra ? 'CGST + SGST' : 'IGST'}</b></span>
            <span>PAN <b className="font-mono">{vendor.pan || '—'}</b>{vendor.pan ? ` (${vendor.pan[3] === 'C' ? 'company' : 'non-company'})` : ''}</span>
          </>
        ) : 'No vendor — for petty cash or one-off bills. Pick a vendor to check GSTIN, state and TDS limits.'}
      </div>

      <Sec>Particulars</Sec>
      <div className="overflow-x-auto rounded-lg border border-[var(--border-color)]">
        <table className="w-full min-w-[720px] text-xs">
          <thead className="bg-white/[0.03] text-left text-[var(--text-muted)]">
            <tr><th className="w-[44px] px-2 py-1.5">Dr/Cr</th><th className="px-2 py-1.5">Particulars</th><th className="px-2 py-1.5 text-right">Debit</th><th className="px-2 py-1.5 text-right">Credit</th></tr>
          </thead>
          <tbody>
            {/* Dr — the expense head, with its department (and any GST that can't be claimed). */}
            <tr className="border-t border-[var(--border-color)] align-top">
              <td className="px-2 py-1.5 font-bold">Dr</td>
              <td className="px-2 py-1.5">
                <SearchableSelect options={heads.map((h) => ({ value: h.id, label: h.name, sublabel: h.group }))} value={s.head} placeholder="Expense head" onChange={(v) => pickHead(v === '' ? '' : Number(v))} />
                <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-[var(--text-muted)]">
                  Department
                  <Select className="!w-auto" value={s.dept} onChange={(e) => set({ dept: e.target.value === '' ? '' : Number(e.target.value) })}>
                    <option value="">— not allocated —</option>
                    {(ccQ.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                  </Select>
                  · Cur Bal <b className="text-[var(--text-primary)]">{curBal(head)}</b>
                </div>
                {bud && head && (
                  <div className="mt-1 text-[11px] text-[var(--text-muted)]">
                    Budget for {head.name} in {MON[Number(bud.month.slice(5)) - 1]}-{bud.month.slice(2, 4)}: {inr(bud.budget)} · spent {inr(bud.spent)} · after this bill {inr(r2(bud.spent + cost))}{' '}
                    <b className={bud.spent + cost > bud.budget ? 'text-danger' : 'text-success'}>({Math.round(((bud.spent + cost) / bud.budget) * 100)}%)</b>
                  </div>
                )}
                {why && <div className="mt-1 text-[11px] text-warning">+ GST {inr(gst)} not claimable ({why}) — added to the expense</div>}
              </td>
              <td className="px-2 py-1.5 text-right">{amt(s.taxable, (v) => set({ taxable: v, gstAmt: null, tdsAmt: null }))}{why ? <div className="mt-1 text-[11px] text-[var(--text-muted)]">= {inr(cost)}</div> : null}</td>
              <td />
            </tr>
            {/* Dr — input GST, or the GST picker when there is none. */}
            <tr className="border-t border-[var(--border-color)] align-top">
              <td className="px-2 py-1.5 font-bold">{itc ? 'Dr' : ''}</td>
              <td className="px-2 py-1.5">
                {itc ? (intra ? <>Input CGST {inr(cg)} + Input SGST {inr(sg)}</> : 'Input IGST') : <span className="text-[var(--text-muted)]">{reg ? (gst > 0 ? 'GST' : 'No GST on this bill') : 'Unregistered vendor — no GST is charged'}</span>}
                <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-[var(--text-muted)]">
                  GST
                  <Select className="!w-auto" disabled={!reg} value={g} onChange={(e) => set({ g: Number(e.target.value), gstAmt: null })}>
                    {GST.map((x) => <option key={x} value={x}>{x ? `${x}%` : 'No GST'}</option>)}
                  </Select>
                  {gst > 0 && !blocked && (
                    <label className="inline-flex items-center gap-1"><input type="checkbox" checked={!s.itcOff} onChange={(e) => set({ itcOff: !e.target.checked })} /> Claim ITC</label>
                  )}
                  {edited(s.gstAmt != null, () => set({ gstAmt: null }))}
                </div>
              </td>
              <td className="px-2 py-1.5 text-right">{itc ? amt(gst, (v) => set({ gstAmt: v })) : null}</td>
              <td />
            </tr>
            {vendor && (
              <>
                {/* Cr — TDS deducted, the section picked on the line. */}
                <tr className="border-t border-[var(--border-color)] align-top">
                  <td className="px-2 py-1.5 font-bold">Cr</td>
                  <td className="px-2 py-1.5">
                    <span className="inline-flex flex-wrap items-center gap-1.5">TDS Payable
                      <Select className="!w-auto" value={s.sec} onChange={(e) => set({ sec: e.target.value, tdsAmt: null })}>
                        <option value="">No TDS</option>
                        {sections.map((x) => <option key={x.code} value={x.code}>{x.code === 'RENT' ? 'Rent*' : x.code} · {x.nature} ({x.oldSection})</option>)}
                      </Select>
                      {edited(s.tdsAmt != null, () => set({ tdsAmt: null }))}
                    </span>
                    {s.sec && tdsQ.data && (
                      <div className="mt-1 text-[11px] text-[var(--text-muted)]">{!/^[A-Z]{5}\d{4}[A-Z]$/.test(tdsQ.data.pan ?? '') ? 'No valid PAN' : `${tdsQ.data.company ? 'Company' : 'Individual / firm'} per PAN`} · {tdsQ.data.reason} · {tdsQ.data.rate}% on {inr(tdsQ.data.base)}</div>
                    )}
                  </td>
                  <td />
                  <td className="px-2 py-1.5 text-right">{amt(tds, (v) => set({ tdsAmt: v }), !s.sec)}</td>
                </tr>
                {/* Cr — the vendor, with this bill as a New Ref. */}
                <tr className="border-t border-[var(--border-color)] align-top">
                  <td className="px-2 py-1.5 font-bold">Cr</td>
                  <td className="px-2 py-1.5">
                    {vendor.name} <span className="text-[11px] text-[var(--text-muted)]">· Cur Bal {curBal(vendor)}</span>
                    <div className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px] text-[var(--text-muted)]">
                      Bill-wise: <b className="text-[var(--text-primary)]">New Ref</b>
                      <Input className="!w-[170px] font-mono" maxLength={60} value={s.billNo} placeholder="Supplier invoice no. *" onChange={(e) => set({ billNo: e.target.value })} />
                      dated <Input type="date" className="!w-[150px]" max={s.date} value={s.billDate} title="Supplier invoice date" onChange={(e) => set({ billDate: e.target.value || s.date })} />
                      due {dd(dueOn)}
                    </div>
                  </td>
                  <td />
                  <td className="px-2 py-1.5 text-right font-mono">{inr(net)}</td>
                </tr>
              </>
            )}
            {!vendor && (bankPart > 0 || cashPart > 0) && billLines.filter((x) => x.a < 0 && x.a !== 0).map((x) => (
              <tr key={x.l} className="border-t border-[var(--border-color)]"><td className="px-2 py-1.5 font-bold">Cr</td><td className="px-2 py-1.5">{x.l}</td><td /><td className="px-2 py-1.5 text-right font-mono">{inr(-x.a)}</td></tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-[var(--border-color)] font-bold">
              <td />
              <td className="px-2 py-1.5 text-right">Total <span className="text-success">✓ balanced</span></td>
              <td className="px-2 py-1.5 text-right font-mono">{inr(r2(cost + (itc ? gst : 0)))}</td>
              <td className="px-2 py-1.5 text-right font-mono">{inr(r2(cost + (itc ? gst : 0)))}</td>
            </tr>
          </tfoot>
        </table>
      </div>

      <Sec>Settlement</Sec>
      {altering ? (
        <p className="text-xs text-[var(--text-muted)]">
          {vendor ? 'Payments made with this bill stay as they are — alter them from Payment.' : `Paid ${s.via === 'split' ? 'from the bank and in cash' : s.via === 'cash' ? 'in cash' : 'from the bank'}.`}
        </p>
      ) : (
        <Grid>
          <F label="Paid via *" className="col-span-2">
            <Select value={viaValue} onChange={(e) => {
              const [v, id] = e.target.value.split(':');
              set({ via: v as PayVia, ...(id ? { bank: Number(id) } : {}), bankAmt: null });
            }}>
              {vendor && <option value="credit">On credit — pay {vendor.name} later</option>}
              {banks.map((b) => <option key={b.id} value={`bank:${b.id}`}>{b.name} — paid now</option>)}
              {cashL && <option value="cash">Cash — paid now</option>}
              {banks.map((b) => <option key={`s${b.id}`} value={`split:${b.id}`}>Split — part {b.name} (cheque / NEFT), part cash</option>)}
            </Select>
          </F>
          {(s.via === 'bank' || s.via === 'split') && (
            <>
              <F label="Mode"><Select value={s.mode} onChange={(e) => set({ mode: e.target.value })}>{BANK_MODES.map((m) => <option key={m}>{m}</option>)}</Select></F>
              <F label="UTR / cheque *"><Input maxLength={60} value={s.ref} onChange={(e) => set({ ref: e.target.value })} /></F>
            </>
          )}
          {s.via === 'split' && (
            <>
              <F label="From bank *"><Input type="number" step="0.01" min={0} className="text-right font-mono" value={bankPart || ''} onChange={(e) => set({ bankAmt: Math.min(due, r2(Number(e.target.value) || 0)) })} /></F>
              <F label="In cash"><Input type="number" step="0.01" min={0} className="text-right font-mono" value={cashPart || ''} onChange={(e) => set({ bankAmt: Math.max(0, r2(due - (Number(e.target.value) || 0))) })} /></F>
            </>
          )}
        </Grid>
      )}
      {!altering && s.via === 'credit' && vendor && (
        <div className="mt-2 rounded-md border border-info/30 bg-info/10 px-2.5 py-1.5 text-xs text-[var(--text-secondary)]">
          Booked as a payable to <b>{vendor.name}</b>, due {dd(dueOn)} ({credit} days from the supplier&rsquo;s invoice date) — settle it from <b>Payment</b>.
        </div>
      )}
      {!altering && s.via === 'split' && (
        <div className="mt-2 rounded-md border border-info/30 bg-info/10 px-2.5 py-1.5 text-xs text-[var(--text-secondary)]">
          Paid in two parts: <b>{inr(bankPart)}</b> from {bankL?.name} ({s.mode}{s.ref.trim() ? ` ${s.ref.trim()}` : ''}) + <b>{inr(cashPart)}</b> in cash = {inr(due)}.
          {vendor ? ' The bill is booked as a Journal, then two Payments are made against it — one from the bank, one from cash.' : ' One Payment voucher, credited to the bank and to cash.'}
        </div>
      )}
      {!altering && payNow && s.via !== 'split' && (
        <div className="mt-2 rounded-md border border-info/30 bg-info/10 px-2.5 py-1.5 text-xs text-[var(--text-secondary)]">
          The bill is booked and paid together — a Journal for the bill and a Payment of {inr(net)} from {s.via === 'cash' ? 'cash' : bankL?.name}.
        </div>
      )}

      <Msgs block={block} warn={warn} />
      <F label="Narration" className="mt-3">
        <Textarea rows={2} maxLength={1000} value={s.narr} placeholder={`${head?.name ?? 'Expense'} — ${vendor?.name || s.paidTo || 'bill'}`} onChange={(e) => set({ narr: e.target.value })} />
      </F>
      <Sec>Posting{payNow && !altering ? ' — 1. Bill' : ''}</Sec>
      <Posting lines={billLines} />
      {payNow && !altering && (
        <>
          <Sec>Posting — 2. Payment{s.via === 'split' ? 's (bank + cash)' : ''}</Sec>
          <Posting lines={payLines} />
        </>
      )}
    </Modal>
  );
}
